import { SITE } from './site';

/**
 * The cities we will actually send a crew to, with enough real, DISTINCT detail per
 * city to justify a page of its own.
 *
 * ─── WHY THESE PAGES EXIST ──────────────────────────────────────────────────────
 * "commercial cleaning tampa" and "janitorial services st petersburg" are the highest
 * intent queries in this business, and until now the only page mentioning those cities
 * was /service-area, which gave each of them about one sentence. A page cannot rank
 * for a query it barely contains.
 *
 * ─── WHY EACH ENTRY IS WRITTEN OUT BY HAND ──────────────────────────────────────
 * The obvious shortcut is one template with the city name swapped in. That is a
 * doorway page — Google's own term for it — and it is an explicit spam policy
 * violation, not a grey area. So every field below is specific to that city: the real
 * commercial corridors, the building stock actually there, the county and therefore
 * the sales-tax rate, and the honest drive time from Brandon. If a field cannot be
 * filled in truthfully for a city, that city does not get a page.
 *
 * ─── THE COUNTY IS NOT DECORATION ───────────────────────────────────────────────
 * `county` selects the sales-tax rate from SALES_TAX in shared/pricing, which is a
 * number that appears on the customer's invoice. It is derived, never re-typed.
 * ────────────────────────────────────────────────────────────────────────────────
 */

export interface City {
  /** URL segment. Must stay stable — it is a published URL. */
  slug: string;
  /** Display name, exactly as SITE.serviceCities spells it. */
  name: string;
  county: 'Hillsborough' | 'Pinellas' | 'Pasco' | 'Polk';
  /** Honest drive time from the Brandon base, off-peak. Crews travel after rush hour. */
  driveFromBase: string;
  /** One sentence a local would recognise as true. Opens the page. */
  lede: string;
  /** The commercial areas we actually work, named. */
  corridors: string[];
  /** The building types that dominate this city's commercial stock. */
  buildingStock: string;
  /** Why the after-hours model matters HERE specifically. Must differ per city. */
  whyAfterHours: string;
  /** The question this city's buyers ask that others do not. */
  localQuestion: { q: string; a: string };
}

export const CITIES: readonly City[] = [
  {
    slug: 'tampa',
    name: 'Tampa',
    county: 'Hillsborough',
    driveFromBase: 'about 25 minutes',
    lede: 'Tampa has the densest commercial office stock in the bay, and the most cleaning companies chasing it. Most of them will not tell you a price.',
    corridors: [
      'Downtown and the Channel District',
      'Westshore business district',
      'Ybor City',
      'Carrollwood and North Dale Mabry',
      'Brandon-adjacent East Tampa',
    ],
    buildingStock:
      'Multi-storey offices with shared lobbies and elevators, converted warehouse space in Ybor, and ground-floor professional suites along Dale Mabry.',
    whyAfterHours:
      'Westshore and Downtown tenants keep long days and share common areas with other firms, so a daytime crew is working around people the entire visit. Cleaning after the floor empties is the only way the common areas actually get finished.',
    localQuestion: {
      q: 'Do you work in multi-tenant buildings where we do not control the whole floor?',
      a: 'Yes — that is most of what we clean in Tampa. We clean the suite you lease and, where the landlord contracts it separately, we stay out of the common areas. The scope is written into the quote so there is no argument later about who was supposed to do the lobby.',
    },
  },
  {
    slug: 'brandon',
    name: 'Brandon',
    county: 'Hillsborough',
    driveFromBase: 'this is our base',
    lede: 'Brandon is home. The crew lives here, the vans start here, and a callback in Brandon does not cost us a drive across the bay.',
    corridors: [
      'Brandon Boulevard / SR 60',
      'Providence Lakes and the Brandon Town Center area',
      'Bloomingdale Avenue',
      'Lumsden Road professional offices',
    ],
    buildingStock:
      'Single-storey professional and medical suites, dental practices, churches, and small strip-plaza retail — the stock a growing suburb builds.',
    whyAfterHours:
      'Brandon practices and offices run full appointment books until early evening. Being based here means we can start as soon as your last patient leaves, rather than queuing you behind a route that started in Pinellas.',
    localQuestion: {
      q: 'How fast can you get here if something is wrong?',
      a: 'We are based in Brandon, so for a Brandon building the answer is usually the same day. That is not a promise we can make everywhere in the bay, which is exactly why we say it here and not on every page.',
    },
  },
  {
    slug: 'riverview',
    name: 'Riverview',
    county: 'Hillsborough',
    driveFromBase: 'about 15 minutes',
    lede: 'Riverview has been building commercial space faster than it has been building cleaning companies to service it.',
    corridors: [
      'US-301 corridor',
      'Big Bend Road',
      'Gibsonton Drive and the Riverview Town Center area',
      'Boyette Road professional plazas',
    ],
    buildingStock:
      'New-build professional plazas, urgent care and specialty medical, churches, and light-industrial flex space near US-301.',
    whyAfterHours:
      'Much of Riverview commercial space is new construction, which means the first clean is often a post-construction one and the recurring schedule starts before the tenant has fully moved in. We do both, and the second is priced separately from the first.',
    localQuestion: {
      q: 'Can you do the post-construction clean and then stay on for the recurring service?',
      a: 'Yes, and they are quoted as two different things. Post-construction is $0.40 per square foot for a vacant space with normal debris; the recurring nightly rate is per square foot per visit and drops as frequency rises. Bundling them into one number is how a contractor hides the ongoing price.',
    },
  },
  {
    slug: 'st-petersburg',
    name: 'St. Petersburg',
    county: 'Pinellas',
    driveFromBase: 'about 40 minutes',
    lede: 'St. Pete runs on small professional firms, creative studios and medical practices packed into older buildings that were not designed around a janitor closet.',
    corridors: [
      'Downtown St. Petersburg and the Edge District',
      'Central Avenue',
      'Gateway / Carillon',
      '4th Street North corridor',
    ],
    buildingStock:
      'Historic downtown buildings converted to office use, modern Class-A space at Carillon, and a lot of independent medical and dental along 4th Street.',
    whyAfterHours:
      'Downtown St. Pete buildings often share one service entrance and one elevator with several tenants. Working after hours means we are not competing with a delivery driver and three other vendors for the same freight lift.',
    localQuestion: {
      q: 'Pinellas is a different county — does the price change?',
      a: 'The cleaning rate does not. The sales tax does: commercial cleaning is taxable in Florida, and Pinellas charges 7% against Hillsborough’s 7.5%. It shows as its own line on the invoice, so you can see exactly what is service and what is tax.',
    },
  },
  {
    slug: 'clearwater',
    name: 'Clearwater',
    county: 'Pinellas',
    driveFromBase: 'about 50 minutes',
    lede: 'Clearwater mixes corporate campuses, medical, and a hospitality-adjacent economy that judges a building on how it looks to a visitor.',
    corridors: [
      'US-19 corridor',
      'Downtown Clearwater and Cleveland Street',
      'Gulf-to-Bay Boulevard',
      'Clearwater business park and the Bayside Bridge approach',
    ],
    buildingStock:
      'Corporate office parks off US-19, standalone medical and specialty practices, churches, and multi-tenant buildings with heavy visitor traffic.',
    whyAfterHours:
      'Clearwater buildings see a lot of walk-in visitors, which means the entryway glass, the lobby floor and the restrooms are the first thing a client judges. Those are exactly the surfaces that cannot be cleaned properly while people are walking over them.',
    localQuestion: {
      q: 'Do you handle entry glass and pressure washing as well as the interior?',
      a: 'Yes, quoted per job or folded into a recurring schedule — quarterly is the usual rhythm for entryways, sidewalks and dumpster pads. For a building where visitors form an opinion in the first ten feet, it is usually worth putting on the schedule rather than calling it in when it looks bad.',
    },
  },
  {
    slug: 'lakeland',
    name: 'Lakeland',
    county: 'Polk',
    driveFromBase: 'about 45 minutes',
    lede: 'Lakeland is Polk County commercial work — more industrial and warehouse, more churches, and buyers who expect a straight number rather than a sales process.',
    corridors: [
      'Downtown Lakeland',
      'US-98 North corridor',
      'Interstate 4 logistics and flex space',
      'South Florida Avenue professional offices',
    ],
    buildingStock:
      'Warehouse and light-industrial flex space near I-4, established churches with large fellowship halls and classroom wings, and professional offices downtown.',
    whyAfterHours:
      'Warehouse and distribution space in Lakeland often runs a day shift straight through. Cleaning the offices, breakrooms and restrooms after that shift ends is the only window where the work does not interrupt production.',
    localQuestion: {
      q: 'We are a church, not an office. Is that different?',
      a: 'Different rhythm, same crew. Sanctuaries, fellowship halls, classrooms and nurseries get cleaned on the schedule that has the building ready by Sunday morning, which usually means a mid-week visit and a Saturday one rather than nightly. Nobody should be staying late to vacuum.',
    },
  },
  {
    slug: 'wesley-chapel',
    name: 'Wesley Chapel',
    county: 'Pasco',
    driveFromBase: 'about 45 minutes',
    lede: 'Wesley Chapel is newer than the rest of the bay, and most of its commercial stock is medical, dental and professional suites built in the last fifteen years.',
    corridors: [
      'SR-56 and the Wiregrass area',
      'Bruce B. Downs Boulevard',
      'SR-54 corridor',
      'Wesley Chapel medical district',
    ],
    buildingStock:
      'Purpose-built medical and dental suites, specialty practices, newer multi-tenant professional plazas, and a growing number of churches.',
    whyAfterHours:
      'Wesley Chapel is dominated by clinical space, and clinical space cannot be cleaned around patients. Evening service means exam rooms, waiting areas and restrooms get the full protocol with nobody in the building.',
    localQuestion: {
      q: 'Can you document the clean for a medical inspection?',
      a: 'Yes. Every visit closes with a signed checklist recording what was cleaned, by whom, and when. For clinical spaces the disinfection steps are itemised on that checklist rather than assumed, which is the form an inspector actually wants to see.',
    },
  },
];

export function cityBySlug(slug: string): City | undefined {
  return CITIES.find((c) => c.slug === slug);
}

/**
 * Guard: every city page must correspond to a city we actually claim to serve.
 *
 * SITE.serviceCities drives /service-area and the schema.org areaServed block. If a
 * city page exists for somewhere that list does not include, the site is advertising
 * coverage its own structured data denies. Called by the SEO build audit.
 */
export function citiesNotInServiceArea(): string[] {
  const served = new Set<string>(SITE.serviceCities);
  return CITIES.filter((c) => !served.has(c.name)).map((c) => c.name);
}
